import React, { useReducer, useEffect, useCallback } from "react";
import { Avatar, Button, Tabs, notification, Spin } from "antd";
import styled from "styled-components";
import moment from "moment";
import { UserOutlined, BranchesOutlined } from "@ant-design/icons";
import { MainLayout } from "../../layouts";
import DetailsTabPane from "./DetailsTabPane";
import TimelineTabPane from "./TimelineTabPane";
import MemoriesTabPane from "./MemoriesTabPane";
import { useParams, Link } from "react-router-dom";
import axios, { CancelTokenSource, AxiosError, AxiosResponse } from "axios";
import { IFamilyTreePerson } from "../../interfaces";
import { Backend } from "../../helpers";

const AvatarIcon = styled(UserOutlined)`
  font-size: 6rem;
`;

export declare type PersonDetailsState = {
  loading: boolean;
  person?: IFamilyTreePerson;
  loadPersonTS: CancelTokenSource;
};

export declare type PersonDetailsReducerAction = {
  type: "updateState" | "startLoading" | "stopLoading";
  state?: Partial<PersonDetailsState>;
};

const PersonDetails: React.FC = (props) => {
  let { id } = useParams();

  const reducer = (
    state: PersonDetailsState,
    action: PersonDetailsReducerAction
  ): PersonDetailsState => {
    switch (action.type) {
      case "updateState": {
        return {
          loading: action.state?.loading ?? state.loading,
          person: action.state?.person ?? state.person,
          loadPersonTS: action.state?.loadPersonTS ?? state.loadPersonTS,
        };
      }
      case "startLoading": {
        const { loading, ...rest } = state;
        return {
          loading: true,
          ...rest,
        };
      }
      case "stopLoading": {
        const { loading, ...rest } = state;
        return {
          loading: false,
          ...rest,
        };
      }
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, {
    loading: false,
    loadPersonTS: axios.CancelToken.source(),
  });

  const loadPerson = useCallback(async () => {
    // start loading
    dispatch({ type: "startLoading" });
    // fetch person
    await Backend.getInstance()
      .axios()
      .get("/api/editnode/", {
        params: {
          personId: id,
        },
        cancelToken: state.loadPersonTS.token,
      })
      .then((response: AxiosResponse<IFamilyTreePerson>) => {
        dispatch({
          type: "updateState",
          state: {
            person: response.data,
          },
        });
      })
      .catch((error: AxiosError<any>) => {
        if (!axios.isCancel(error)) {
          notification.warn({
            message: "Failed to load person details. Refresh the page.",
          });
        }
      })
      .finally(() => {
        dispatch({ type: "stopLoading" });
      });
  }, [state.loadPersonTS.token, id]);

  useEffect(() => {
    loadPerson();
    return () => {
      state.loadPersonTS.cancel();
    };
  }, [state.loadPersonTS, loadPerson]);

  return (
    <MainLayout fixed={true} className="bg-or_gray-100">
      <Spin spinning={state.loading} size="large">
        {state.person && (
          <div className="flex items-center p-4 sm:p-8 mt-8 mb-3 bg-white">
            <Avatar
              className="cursor-pointer w-24 h-24"
              size={"default"}
              icon={<AvatarIcon />}
            />
            <div className="flex-auto mx-4">
              <div className="text-lg font-bold">
                {[
                  state.person.first_name,
                  state.person.middle_name,
                  state.person.last_name,
                ].join(" ")}
              </div>
              <div className="text-sm">
                {moment(state.person.dob, "YYYY-MM-DD").format("DD-MMMM-YYYY")}
              </div>
            </div>
            <div>
              <Link to={`/family-tree?rootId=${state.person.id}`}>
                <Button
                  type="default"
                  className="rounded-full uppercase"
                  icon={<BranchesOutlined />}
                >
                  view tree
                </Button>
              </Link>
            </div>
          </div>
        )}

        <div className="mx-4 sm:mx-0 mb-8">
          <Tabs size="large" defaultActiveKey="1">
            <Tabs.TabPane tab="Details" key="1">
              {state.person && <DetailsTabPane person={state.person} />}
            </Tabs.TabPane>
            <Tabs.TabPane className="bg-white p-8" tab="Timeline" key="2">
              <TimelineTabPane />
            </Tabs.TabPane>
            <Tabs.TabPane tab="Memories" key="3">
              <MemoriesTabPane />
            </Tabs.TabPane>
          </Tabs>
        </div>
      </Spin>
    </MainLayout>
  );
};

export default PersonDetails;
